import axios from 'axios';

// const API_BASE_URL = '/api/v1'; // Assuming a base URL for all API calls

const carePlanningService = {
  getCarePlanningStatus: async (patientId) => {
    if (!patientId) {
      throw new Error('Patient ID is required to fetch care planning status.');
    }
    // Axios instance might prepend /api/v1, so adjust path accordingly
    // If axios is configured with baseURL='/api/v1', then path should be `/patients/${patientId}/care-planning/status`
    // If not, path should be `/api/v1/patients/${patientId}/care-planning/status`
    // Based on previous fixes (e.g. FlowsheetDataManagementPanel), axios likely prepends /api/v1.
    const response = await axios.get(`/patients/${patientId}/care-planning/status`);
    return response.data; // Expected: { status: 'success', data: { Vitals: { status, ... }, ... } }
  },

  submitCarePlanningData: async (patientId, sectionName, formData, entryDate) => {
    if (!patientId || !sectionName || !formData || !entryDate) {
      throw new Error('Missing required parameters for submitting care planning data.');
    }
    const payload = {
      formData,
      entryDate: entryDate.toISOString(), // Ensure entryDate is ISO string
    };
    const response = await axios.post(`/patients/${patientId}/care-planning/data/${sectionName}`, payload);
    return response.data; // Expected: { status: 'success', message: '...', data: { section, status, ... } }
  },

  getCarePlanningSectionData: async (patientId, sectionName) => {
    if (!patientId || !sectionName) {
      throw new Error('Patient ID and section name are required to fetch section data.');
    }
    // entryDate query parameter is no longer needed.
    const response = await axios.get(`/patients/${patientId}/care-planning/data/${sectionName}`);
    return response.data; // Expected: { status: 'success', data: { fieldName1: value1, ... } }
  },

  submitEKGCarePlanningData: async (patientId, formData, entryDate) => {
    if (!patientId || !formData || !entryDate) {
      throw new Error('Missing required parameters for submitting EKG care planning data.');
    }

    try {
      const formDataObj = new FormData();

      // Add the EKG findings data
      formDataObj.append('formData', JSON.stringify(formData.findings || []));
      formDataObj.append('entryDate', entryDate.toISOString());

      // Add document information
      formDataObj.append('documentType', formData.documentType || 'file');

      if (formData.externalLink) {
        formDataObj.append('externalLink', formData.externalLink);
      }

      if (formData.existingDocumentId) {
        formDataObj.append('existingDocumentId', formData.existingDocumentId);
      }

      // Add the file if present
      if (formData.file) {
        // Handle both native File objects and Ant Design Upload file objects
        const fileToUpload = formData.file.originFileObj || formData.file;
        console.log('File to upload:', fileToUpload);
        console.log('File type:', typeof fileToUpload);
        console.log('Is File instance:', fileToUpload instanceof File);
        formDataObj.append('ekgFile', fileToUpload);
      }

      console.log('Sending EKG data to:', `/patients/${patientId}/care-planning/data/EKG/with-file`);
      console.log('Full URL:', axios.defaults.baseURL + `/patients/${patientId}/care-planning/data/EKG/with-file`);
      console.log('FormData contents:', {
        formData: JSON.stringify(formData.findings || []),
        entryDate: entryDate.toISOString(),
        hasFile: !!formData.file,
        fileName: formData.file?.name
      });

      const response = await axios.post(`/patients/${patientId}/care-planning/data/EKG/with-file`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('EKG submission error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // If the endpoint doesn't exist (404, 405) or server error (500), fall back to regular EKG submission without file
      if (error.response?.status === 404 || error.response?.status === 405 || error.response?.status === 500) {
        console.warn('EKG file upload endpoint not available, falling back to regular submission');
        console.warn('Error details:', error.response?.data);
        console.warn('Note: File upload will be skipped in fallback mode');

        // Convert findings to the format expected by regular endpoint
        const regularFormData = formData.findings || [];
        const payload = {
          formData: regularFormData,
          entryDate: entryDate.toISOString(),
        };

        try {
          const fallbackResponse = await axios.post(`/patients/${patientId}/care-planning/data/EKG`, payload);
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  },

  getEKGCarePlanningData: async (patientId) => {
    if (!patientId) {
      throw new Error('Patient ID is required to fetch EKG care planning data.');
    }

    try {
      console.log('Fetching EKG data for patient:', patientId);
      const response = await axios.get(`/patients/${patientId}/care-planning/data/EKG/details`);
      console.log('EKG data retrieved:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching EKG data:', error);

      // If the endpoint doesn't exist, return empty data
      if (error.response?.status === 404) {
        console.warn('EKG details endpoint not available, returning empty data');
        return { status: 'success', data: { details: [], document: null } };
      }

      throw error;
    }
  },

  // Submit Chest XR care planning data with file upload
  submitChestXRCarePlanningData: async (patientId, formData, entryDate) => {
    if (!patientId || !entryDate) {
      throw new Error('Patient ID and entry date are required to submit Chest XR care planning data.');
    }

    try {
      const formDataObj = new FormData();

      // Add form data as JSON string
      formDataObj.append('formData', JSON.stringify(formData.findings || []));
      formDataObj.append('entryDate', entryDate.toISOString());
      formDataObj.append('documentType', formData.documentType || 'file');

      // Add file if provided
      if (formData.file) {
        formDataObj.append('chestXRFile', formData.file);
      }

      // Add external link if provided
      if (formData.externalLink) {
        formDataObj.append('externalLink', formData.externalLink);
      }

      // Add existing document ID for updates
      if (formData.existingDocumentId) {
        formDataObj.append('existingDocumentId', formData.existingDocumentId);
      }

      console.log('Sending Chest XR data to:', `/patients/${patientId}/care-planning/data/ChestXR/with-file`);
      console.log('Full URL:', axios.defaults.baseURL + `/patients/${patientId}/care-planning/data/ChestXR/with-file`);
      console.log('FormData contents:', {
        formData: JSON.stringify(formData.findings || []),
        entryDate: entryDate.toISOString(),
        hasFile: !!formData.file,
        fileName: formData.file?.name
      });

      const response = await axios.post(`/patients/${patientId}/care-planning/data/ChestXR/with-file`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Chest XR submission error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // If the endpoint doesn't exist (404, 405) or server error (500), fall back to regular Chest XR submission without file
      if (error.response?.status === 404 || error.response?.status === 405 || error.response?.status === 500) {
        console.warn('Chest XR file upload endpoint not available, falling back to regular submission');
        console.warn('Error details:', error.response?.data);
        console.warn('Note: File upload will be skipped in fallback mode');

        // Convert findings to the format expected by regular endpoint
        const regularFormData = formData.findings || [];
        const payload = {
          formData: regularFormData,
          entryDate: entryDate.toISOString(),
        };

        try {
          const fallbackResponse = await axios.post(`/patients/${patientId}/care-planning/data/ChestXR`, payload);
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Fallback Chest XR submission also failed:', fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  },

  getChestXRCarePlanningData: async (patientId) => {
    if (!patientId) {
      throw new Error('Patient ID is required to fetch Chest XR care planning data.');
    }

    try {
      console.log('Fetching Chest XR data for patient:', patientId);
      const response = await axios.get(`/patients/${patientId}/care-planning/data/ChestXR/details`);
      console.log('Chest XR data retrieved:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching Chest XR data:', error);

      // If the endpoint doesn't exist, return empty data
      if (error.response?.status === 404) {
        console.warn('Chest XR details endpoint not available, returning empty data');
        return { status: 'success', data: { details: [], document: null } };
      }

      throw error;
    }
  },

  // Submit Thyroid US care planning data with file upload
  submitThyroidUSCarePlanningData: async (patientId, formData, entryDate) => {
    if (!patientId || !entryDate) {
      throw new Error('Patient ID and entry date are required to submit Thyroid US care planning data.');
    }

    try {
      const formDataObj = new FormData();

      // Add form data as JSON string
      formDataObj.append('formData', JSON.stringify(formData.findings || []));
      formDataObj.append('entryDate', entryDate.toISOString());
      formDataObj.append('documentType', formData.documentType || 'file');

      // Add file if provided
      if (formData.file) {
        formDataObj.append('thyroidUSFile', formData.file);
      }

      // Add external link if provided
      if (formData.externalLink) {
        formDataObj.append('externalLink', formData.externalLink);
      }

      // Add existing document ID for updates
      if (formData.existingDocumentId) {
        formDataObj.append('existingDocumentId', formData.existingDocumentId);
      }

      console.log('Sending Thyroid US data to:', `/patients/${patientId}/care-planning/data/ThyroidUS/with-file`);
      console.log('Full URL:', axios.defaults.baseURL + `/patients/${patientId}/care-planning/data/ThyroidUS/with-file`);
      console.log('FormData contents:', {
        formData: JSON.stringify(formData.findings || []),
        entryDate: entryDate.toISOString(),
        hasFile: !!formData.file,
        fileName: formData.file?.name
      });

      const response = await axios.post(`/patients/${patientId}/care-planning/data/ThyroidUS/with-file`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Thyroid US submission error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // If the endpoint doesn't exist (404, 405) or server error (500), fall back to regular Thyroid US submission without file
      if (error.response?.status === 404 || error.response?.status === 405 || error.response?.status === 500) {
        console.warn('Thyroid US file upload endpoint not available, falling back to regular submission');
        console.warn('Error details:', error.response?.data);
        console.warn('Note: File upload will be skipped in fallback mode');

        // Convert findings to the format expected by regular endpoint
        const regularFormData = formData.findings || [];
        const payload = {
          formData: regularFormData,
          entryDate: entryDate.toISOString(),
        };

        try {
          const fallbackResponse = await axios.post(`/patients/${patientId}/care-planning/data/ThyroidUS`, payload);
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Fallback Thyroid US submission also failed:', fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  },

  getThyroidUSCarePlanningData: async (patientId) => {
    if (!patientId) {
      throw new Error('Patient ID is required to fetch Thyroid US care planning data.');
    }

    try {
      console.log('Fetching Thyroid US data for patient:', patientId);
      const response = await axios.get(`/patients/${patientId}/care-planning/data/ThyroidUS/details`);
      console.log('Thyroid US data retrieved:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching Thyroid US data:', error);

      // If the endpoint doesn't exist, return empty data
      if (error.response?.status === 404) {
        console.warn('Thyroid US details endpoint not available, returning empty data');
        return { status: 'success', data: { details: [], document: null } };
      }

      throw error;
    }
  },

  // Submit Heart US care planning data with file upload
  submitHeartUSCarePlanningData: async (patientId, formData, entryDate) => {
    if (!patientId || !entryDate) {
      throw new Error('Patient ID and entry date are required to submit Heart US care planning data.');
    }

    try {
      const formDataObj = new FormData();

      // Add form data
      if (formData.findings && formData.findings.length > 0) {
        formDataObj.append('formData', JSON.stringify(formData.findings));
      }

      // Add entry date
      formDataObj.append('entryDate', entryDate.toISOString());

      // Add file if present
      if (formData.file) {
        formDataObj.append('heartUSFile', formData.file);
      }

      // Add external link if present
      if (formData.externalLink) {
        formDataObj.append('externalLink', formData.externalLink);
      }

      // Add document type
      formDataObj.append('documentType', formData.documentType || 'file');

      // Add existing document ID if updating
      if (formData.existingDocumentId) {
        formDataObj.append('existingDocumentId', formData.existingDocumentId);
      }

      console.log('Sending Heart US data to:', `/patients/${patientId}/care-planning/data/HeartUS/with-file`);
      console.log('Full URL:', axios.defaults.baseURL + `/patients/${patientId}/care-planning/data/HeartUS/with-file`);
      console.log('FormData contents:', {
        formData: JSON.stringify(formData.findings || []),
        entryDate: entryDate.toISOString(),
        hasFile: !!formData.file,
        fileName: formData.file?.name
      });

      const response = await axios.post(`/patients/${patientId}/care-planning/data/HeartUS/with-file`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Heart US submission successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error submitting Heart US care planning data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // If the file upload endpoint doesn't exist, try fallback without file
      if (error.response?.status === 404 && error.response?.data?.message?.includes('Cannot POST')) {
        console.warn('Heart US file upload endpoint not available, trying fallback without file upload');

        const payload = {
          formData: formData.findings || [],
          entryDate: entryDate.toISOString(),
          externalLink: formData.externalLink || null
        };

        try {
          const fallbackResponse = await axios.post(`/patients/${patientId}/care-planning/data/HeartUS`, payload);
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Fallback Heart US submission also failed:', fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  },

  getHeartUSCarePlanningData: async (patientId) => {
    if (!patientId) {
      throw new Error('Patient ID is required to fetch Heart US care planning data.');
    }

    try {
      console.log('Fetching Heart US data for patient:', patientId);
      const response = await axios.get(`/patients/${patientId}/care-planning/data/HeartUS/details`);
      console.log('Heart US data retrieved:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching Heart US data:', error);

      // If the endpoint doesn't exist, return empty data
      if (error.response?.status === 404) {
        console.warn('Heart US details endpoint not available, returning empty data');
        return { status: 'success', data: { details: [], document: null } };
      }

      throw error;
    }
  },

  // Submit Carotid US care planning data with file upload
  submitCarotidUSCarePlanningData: async (patientId, formData, entryDate) => {
    if (!patientId || !entryDate) {
      throw new Error('Patient ID and entry date are required to submit Carotid US care planning data.');
    }

    try {
      const formDataObj = new FormData();

      // Add form data
      if (formData.findings && formData.findings.length > 0) {
        formDataObj.append('formData', JSON.stringify(formData.findings));
      }

      // Add entry date
      formDataObj.append('entryDate', entryDate.toISOString());

      // Add file if present
      if (formData.file) {
        formDataObj.append('carotidUSFile', formData.file);
      }

      // Add external link if present
      if (formData.externalLink) {
        formDataObj.append('externalLink', formData.externalLink);
      }

      // Add document type
      formDataObj.append('documentType', formData.documentType || 'file');

      // Add existing document ID if updating
      if (formData.existingDocumentId) {
        formDataObj.append('existingDocumentId', formData.existingDocumentId);
      }

      console.log('Sending Carotid US data to:', `/patients/${patientId}/care-planning/data/CarotidUS/with-file`);
      console.log('Full URL:', axios.defaults.baseURL + `/patients/${patientId}/care-planning/data/CarotidUS/with-file`);
      console.log('FormData contents:', {
        formData: JSON.stringify(formData.findings || []),
        entryDate: entryDate.toISOString(),
        hasFile: !!formData.file,
        fileName: formData.file?.name
      });

      const response = await axios.post(`/patients/${patientId}/care-planning/data/CarotidUS/with-file`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Carotid US submission successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error submitting Carotid US care planning data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // If the file upload endpoint doesn't exist, try fallback without file
      if (error.response?.status === 404 && error.response?.data?.message?.includes('Cannot POST')) {
        console.warn('Carotid US file upload endpoint not available, trying fallback without file upload');

        const payload = {
          formData: formData.findings || [],
          entryDate: entryDate.toISOString(),
          externalLink: formData.externalLink || null
        };

        try {
          const fallbackResponse = await axios.post(`/patients/${patientId}/care-planning/data/CarotidUS`, payload);
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Fallback Carotid US submission also failed:', fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  },

  getCarotidUSCarePlanningData: async (patientId) => {
    if (!patientId) {
      throw new Error('Patient ID is required to fetch Carotid US care planning data.');
    }

    try {
      console.log('Fetching Carotid US data for patient:', patientId);
      const response = await axios.get(`/patients/${patientId}/care-planning/data/CarotidUS/details`);
      console.log('Carotid US data retrieved:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching Carotid US data:', error);

      // If the endpoint doesn't exist, return empty data
      if (error.response?.status === 404) {
        console.warn('Carotid US details endpoint not available, returning empty data');
        return { status: 'success', data: { details: [], document: null } };
      }

      throw error;
    }
  },

  // Submit Abdominal US care planning data with file upload
  submitAbdominalUSCarePlanningData: async (patientId, formData, entryDate) => {
    if (!patientId || !entryDate) {
      throw new Error('Patient ID and entry date are required to submit Abdominal US care planning data.');
    }

    try {
      const formDataObj = new FormData();

      // Add form data
      if (formData.findings && formData.findings.length > 0) {
        formDataObj.append('formData', JSON.stringify(formData.findings));
      }

      // Add entry date
      formDataObj.append('entryDate', entryDate.toISOString());

      // Add file if present
      if (formData.file) {
        formDataObj.append('abdominalUSFile', formData.file);
      }

      // Add external link if present
      if (formData.externalLink) {
        formDataObj.append('externalLink', formData.externalLink);
      }

      // Add document type
      formDataObj.append('documentType', formData.documentType || 'file');

      // Add existing document ID if updating
      if (formData.existingDocumentId) {
        formDataObj.append('existingDocumentId', formData.existingDocumentId);
      }

      console.log('Sending Abdominal US data to:', `/patients/${patientId}/care-planning/data/AbdominalUS/with-file`);
      console.log('Full URL:', axios.defaults.baseURL + `/patients/${patientId}/care-planning/data/AbdominalUS/with-file`);
      console.log('FormData contents:', {
        formData: JSON.stringify(formData.findings || []),
        entryDate: entryDate.toISOString(),
        hasFile: !!formData.file,
        fileName: formData.file?.name
      });

      const response = await axios.post(`/patients/${patientId}/care-planning/data/AbdominalUS/with-file`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Abdominal US submission successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error submitting Abdominal US care planning data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // If the file upload endpoint doesn't exist, try fallback without file
      if (error.response?.status === 404 && error.response?.data?.message?.includes('Cannot POST')) {
        console.warn('Abdominal US file upload endpoint not available, trying fallback without file upload');

        const payload = {
          formData: formData.findings || [],
          entryDate: entryDate.toISOString(),
          externalLink: formData.externalLink || null
        };

        try {
          const fallbackResponse = await axios.post(`/patients/${patientId}/care-planning/data/AbdominalUS`, payload);
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Fallback Abdominal US submission also failed:', fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  },

  getAbdominalUSCarePlanningData: async (patientId) => {
    if (!patientId) {
      throw new Error('Patient ID is required to fetch Abdominal US care planning data.');
    }

    try {
      console.log('Fetching Abdominal US data for patient:', patientId);
      const response = await axios.get(`/patients/${patientId}/care-planning/data/AbdominalUS/details`);
      console.log('Abdominal US data retrieved:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching Abdominal US data:', error);

      // If the endpoint doesn't exist, return empty data
      if (error.response?.status === 404) {
        console.warn('Abdominal US details endpoint not available, returning empty data');
        return { status: 'success', data: { details: [], document: null } };
      }

      throw error;
    }
  },

  // "Mark as N/A" functionality was removed from the plan.
  // If it were to be re-added, a function like this would be needed:
  // setSectionNotAvailable: async (patientId, sectionName) => {
  //   if (!patientId || !sectionName) {
  //     throw new Error('Patient ID and section name are required.');
  //   }
  //   const response = await axios.put(`/patients/${patientId}/care-planning/status/${sectionName}`, { newStatus: 'NotAvailable' });
  //   return response.data;
  // }
};

export default carePlanningService;
